import React, { useEffect, useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { feesAPI } from '../../services/feesService';
import { classesAPI } from '../../services/classService';
import FeesTable from './FeesTable';
import FeeDialog from './FeeDialog';
import FeeFilter from './FeeFilter';

export default function FeeStructures() {
  const toast = useRef();

  const [loading, setLoading] = useState(false);
  const [feeStructures, setFeeStructures] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [classes, setClasses] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);

  const [lazyParams, setLazyParams] = useState({
    page: 1,
    rows: 4,
    sortField: 'id',
    sortOrder: -1,
    globalFilter: '',
    classId: '',
    feeType: ''
  });

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [feeForm, setFeeForm] = useState({
    feeType: '',
    amount: '',
    classId: '',
    description: ''
  });

  const feeTypeOptions = [
    { label: 'All Fee Types', value: '' },
    ...feeTypes.map(ft => ({ label: ft.feeName, value: ft.feeName }))
  ];

  const loadClasses = async () => {
    try {
      const res = await classesAPI.getAllclasses();
      if (res?.data?.success) setClasses(res.data.data || []);
      else setClasses([]);
    } catch (err) {
      console.error('loadClasses error', err);
      setClasses([]);
    }
  };

  const loadFeeTypes = async () => {
    try {
      const res = await feesAPI.getFeeTypes();
      if (res?.data?.success) setFeeTypes(res.data.data || []);
      else setFeeTypes([]);
    } catch (err) {
      console.error('loadFeeTypes error', err);
      setFeeTypes([]);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        page: lazyParams.page,
        limit: lazyParams.rows,
        search: lazyParams.globalFilter,
        classId: lazyParams.classId,
        feeType: lazyParams.feeType,
        sortField: lazyParams.sortField,
        sortOrder: lazyParams.sortOrder === 1 ? 'asc' : 'desc'
      };

      const res = await feesAPI.getFeeStructures(params);
      if (res.data.success) {
        setFeeStructures(res.data.data.feeStructures);
        setTotalRecords(res.data.data.pagination.total);
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load fee structures' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClasses();
    loadFeeTypes();
  }, []);

  useEffect(() => {
    loadData();
  }, [lazyParams]);

  const onPage = (e) => setLazyParams({ ...lazyParams, page: e.page + 1, rows: e.rows });
  const onSort = (e) => setLazyParams({ ...lazyParams, sortField: e.sortField, sortOrder: e.sortOrder });
  const onSearch = (e) => setLazyParams({ ...lazyParams, globalFilter: e.target.value, page: 1 });
  const onClassFilter = (e) => setLazyParams({ ...lazyParams, classId: e.value, page: 1 });
  const onFeeTypeFilter = (e) => setLazyParams({ ...lazyParams, feeType: e.value, page: 1 });

  const openNew = () => {
    setEditing(null);
    setFeeForm({ feeType: '', amount: '', classId: '', description: '' });
    setDialogVisible(true);
  };

  const editItem = (row) => {
    setEditing(row);
    setFeeForm({
      feeType: row.feeType || '',
      amount: row.amount || '',
      classId: row.classId || row.class_id || '',
      description: row.description || ''
    });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditing(null);
  };

  const saveFee = async () => {
    try {
      const res = editing 
        ? await feesAPI.updateFeeStructure(editing.id, feeForm)
        : await feesAPI.addFeeStructure(feeForm);
      
      toast.current?.show({ severity: 'success', summary: 'Success', detail: res?.data?.message || 'Fee structure saved' });
      hideDialog();
      loadData();
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to save Fee structure' });
    }
  };

  const confirmDelete = (row) => {
    confirmDialog({
      message: 'Are you sure you want to delete this fee structure?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg transition-all shadow-md mx-2',
      rejectClassName: 'bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-2 rounded-lg transition-all mx-2',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'No, Keep it',
      accept: () => handleDelete(row.id),
    });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const res = await feesAPI.deleteFeeStructure(id);
      if (res.data.success) {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Fee structure deleted successfully' });
        loadData();
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete fee structure' });
    } finally {
      setLoading(false);
    }
  };

  const actionsBody = (row) => (
    <div className="flex items-center justify-center space-x-2">
      <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm hover:bg-green-50 text-green-600 border-2 border-green-200 hover:border-green-300 transition-all duration-200" onClick={() => editItem(row)} tooltip="Edit Fee" tooltipOptions={{ position: 'top' }} />
      <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm hover:bg-red-50 text-red-600 border-2 border-red-200 hover:border-red-300 transition-all duration-200" onClick={() => confirmDelete(row)} tooltip="Delete Fee" tooltipOptions={{ position: 'top' }} />
    </div>
  );

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <FeeFilter 
        lazyParams={lazyParams}
        onSearch={onSearch}
        classes={classes}
        onClassFilter={onClassFilter}
        feeTypeOptions={feeTypeOptions}
        onFeeTypeFilter={onFeeTypeFilter}
        openNew={openNew}
      />

      <FeesTable 
        feeStructures={feeStructures}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        lazyParams={lazyParams}
        loading={loading}
        actionsBody={actionsBody}
      />

      <FeeDialog 
        visible={dialogVisible}
        onHide={hideDialog}
        editing={editing}
        feeForm={feeForm}
        setFeeForm={setFeeForm}
        saveFee={saveFee}
        feeTypeOptions={feeTypeOptions}
        classes={classes}
      />
    </div>
  );
}
